import { useEffect, useState } from 'react'
import IssuesSummary from './IssuesSummary'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/swiper-bundle.min.css' // Import Swiper styles
import { Navigation } from 'swiper'
import { Icon } from '@iconify/react'
import ImageCard from './PhaseCard'
import { ReactImageCarouselViewer, Image } from 'react-image-carousel-viewer'
import axiosInstance from './lib/axiosInstance'
import BackgroundImage from '../assets/images/pexels-aukid-phumsirichat-3095759-4691571-bright.jpg'

import { LoadingOutlined } from '@ant-design/icons'
import { Spin } from 'antd'
import { Company, HttpResponse, Issue, Phase } from './commons'

type DashboardBodyProps = {
  selectedProjectId: string
  companyId: string
}

const DashboardBody: React.FC<DashboardBodyProps> = ({ selectedProjectId, companyId }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [phases, setPhases] = useState<Phase[]>([])
  const [slideshowImages, setSlideshowImages] = useState<Image[]>([])
  const [unresolvedCount, setUnresolvedCount] = useState(0)
  const [companyName, setCompanyName] = useState('')
  const [isLoading, setIsLoading] = useState(true) // Add loading state

  useEffect(() => {

    const fetchCompany = async () => {
      try {
        const response = await axiosInstance.get<any, HttpResponse<Company>>(
          `/api/v1/Company/ViewById?CompanyId=${companyId}`
        )
        setCompanyName(response?.data?.data.name)
      } catch (error) {
        console.error('Error fetching issues:', error)
      }
    }

    const fetchPhases = async () => {
      try {
        const response = await axiosInstance.get<any, HttpResponse<Phase[]>>(
          `/api/v1/Phases/GetByProject?ProjectId=${selectedProjectId}`
        )
        setPhases(response?.data?.data)
      } catch (error) {
        console.error('Error fetching phases:', error)
      } finally {
        setIsLoading(false) // Set loading to false after fetch is complete
      }
    }

    const fetchIssues = async () => {
      try {
        const response = await axiosInstance.get<any, HttpResponse<Issue[]>>(
          `/api/v1/Issues/GetByProject?ProjectId=${selectedProjectId}`
        )
        setUnresolvedCount(response?.data?.data.filter(i => i.status === 'Unresolved').length)
      } catch (error) {
        console.error('Error fetching issues:', error)
      }
    }

    const runRequired = async () => {
      await fetchCompany()
      await fetchIssues()
      await fetchPhases()
    }

    runRequired()

  }, [selectedProjectId, companyId])

  const handleViewImage = (index: number) => {
    setCurrentIndex(index)
    setIsOpen(true)
  }

  const updateGallery = (images: string[]) => {
    setSlideshowImages(images.map(url => ({ src: url })));
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <div className="">
      {/* Summary Section */}
      <div 
        style={{ 
          backgroundImage: `url(${BackgroundImage})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }} 
        className="px-10 xl:pt-20 pt-6 xl:pb-20 pb-12 md:px-20 w-full mx-auto">
        <div className="flex flex-wrap -mx-3">
          <div className="w-full py-4 md:p-4">
            <IssuesSummary unresolvedCount={unresolvedCount} projectName={companyName} />
          </div>
        </div>
      </div>

      {/* Swiper Section */}
      <div className=" w-full pt-6 pb-14 px-10 md:px-20">
        <h1 className="font-bold text-xl md:text-xl text-blue-900 opacity-100 px-2">Phases</h1>
        {isLoading ? ( // Show loading text if data is loading
          <div className="flex items-center justify-center mt-20">
            <Spin
              indicator={<LoadingOutlined spin />}
              size="large"
              className="text-black"
            />
          </div>
        ) : phases?.length < 1 ? ( // Show no data text if no data is available
          <p className="text-center text-gray-500">No data available</p>
        ) : (
          <Swiper
            modules={[Navigation]}
            spaceBetween={20}
            slidesPerView={4}
            navigation={{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }}
            breakpoints={{
              // Add breakpoints for responsive behavior
              200: {
                // When screen width is 360px or more
                slidesPerView: 1,
              },
              412: {
                slidesPerView: 2,
              },
              720: {
                slidesPerView: 3,
              },
              1280: {
                slidesPerView: 4,
              },
              1680: {
                slidesPerView: 5,
              },
            }}
            loop={false}
            className="relative"
          >
            {phases?.map((slide, index) => (
              <SwiperSlide key={slide.id}>
                <ImageCard
                  // imageUrl={slide.imageUrl}
                  issues={slide.issues}
                  title={`${(index + 1)}. ${slide?.name}`}
                  status={slide?.status}
                  // images={imagesList}
                  phaseId={slide.id}
                  handleViewImage={handleViewImage}
                  updateGallery={updateGallery}
                />
              </SwiperSlide>
            ))}

            {/* Navigation buttons */}
            <div className="swiper-button-prev absolute top-1/2 -translate-y-1/2 left-4 z-10 cursor-pointer">
              <Icon icon="mingcute:left-fill" className="w-6 h-6" />
            </div>
            <div className="swiper-button-next absolute top-1/2 -translate-y-1/2 right-4 z-10 cursor-pointer">
              <Icon className="w-6 h-6" icon="mingcute:right-fill" />
            </div>
          </Swiper>
        )}
      </div>

      <ReactImageCarouselViewer
        open={isOpen}
        onClose={handleClose}
        images={slideshowImages}
        startIndex={currentIndex}
        className="z-[600]"
      />
    </div>
  )
}

export default DashboardBody
